import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FiGrid, FiPackage, FiLayers, FiTag, FiShoppingCart, FiUsers,
  FiTruck, FiLifeBuoy, FiZap, FiFileText, FiSettings,
  FiChevronDown, FiAward, FiUserCheck, FiArchive, FiBarChart2,
  FiHelpCircle, FiTrash2, FiCreditCard,
  FiDollarSign, FiActivity, FiInbox, FiBell,
} from "react-icons/fi";

/**
 * Maps the English display name that the menu is historically built with
 * to its sidebar.* translation key. Keeps the big `menu` array untouched
 * while letting {t()} render the localized string. Entries not in this
 * map fall through to the original English label.
 */
const MENU_I18N: Record<string, string> = {
  "Dashboard": "sidebar.dashboard",
  "Pending Orders": "sidebar.pendingOrders",
  "Orders": "sidebar.orders",
  "Customers": "sidebar.customers",
  "Products": "sidebar.products",
  "Categories": "sidebar.categories",
  "Brands": "sidebar.brands",
  "Flash Sales": "sidebar.flashSales",
  "Promo Codes": "sidebar.promoCodes",
  "Featured Sections": "sidebar.featuredSections",
  "Home Banners": "sidebar.homeBanners",
  "Legal Pages": "sidebar.legalPages",
  "Blog Posts": "sidebar.blog",
  "Newsletter": "sidebar.newsletter",
  "Notifications": "sidebar.notifications",
  "Support Tickets": "sidebar.supportTickets",
  "Return Requests": "sidebar.returnRequests",
  "Marketing": "sidebar.marketing",
  "Customer Comms": "sidebar.customerComms",
  "Warehouse": "sidebar.warehouse",
  "Delivery Boys": "sidebar.deliveryBoys",
  "Analytics": "sidebar.analytics",
  "Payments": "sidebar.payments",
  "COD Cash": "sidebar.codCash",
  "Reports": "sidebar.reports",
  "VIP Wallets": "sidebar.vipWallets",
  "WhatsApp Templates": "sidebar.whatsappTemplates",
  "App Settings": "sidebar.appSettings",
  "Change Log": "sidebar.auditLogs",
  "Media Library": "sidebar.mediaManager",
  "Admin Users": "sidebar.adminUsers",
  "Settings": "sidebar.settings",
  "Content": "sidebar.content",
  "Support": "sidebar.support",
  "Help & Docs": "sidebar.helpAndDocs",
};

type SidebarProps = {
  isCollapsed: boolean;
};

type MenuItem = {
  icon: React.ReactNode;
  name: string;
  path: string;
  section?: string;
  children?: SubMenuItem[];
};

type SubMenuItem = {
  name: string;
  path: string;
};

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  // Resolve an entry's English label to its Hindi translation when available.
  const label = (name: string): string => {
    const key = MENU_I18N[name];
    return key ? t(key) : name;
  };

  // The order below is tuned for daily use, not the natural reading
  // order of an e-commerce catalog. Sections are arranged top-to-bottom
  // by how often an admin actually opens them:
  //   1. Dashboard — glance
  //   2. Orders/Customers/Payments — the fulfilment workday
  //   3. Warehouse + Delivery — pack/assign/track
  //   4. Customer service — tickets + returns
  //   5. Catalog — products/categories/brands (rare changes)
  //   6. Marketing — campaigns + comms + VIP
  //   7. Content — blog/media/legal
  //   8. Insights — analytics + reports
  //   9. System — settings/admins/docs/trash
  //
  // Pending Orders deliberately appears twice (top-level shortcut +
  // inside Warehouse) because it's the most-opened page; the duplicate
  // hop saves a click on every fulfilment cycle. Industry standard
  // pattern — Shopify, Amazon Seller Central etc. all surface
  // "Orders to fulfil" prominently outside any submenu.
  const menu: MenuItem[] = useMemo(() => [
    // ── MAIN ──────────────────────────────────────────────
    {
      icon: <FiGrid size={18} />,
      name: "Dashboard",
      path: "/admin",
      section: "MAIN",
    },

    // ── ORDERS ────────────────────────────────────────────
    // Fulfilment workhorse. Pending Orders gets a top-level slot
    // because it's the single most-opened page in the panel.
    {
      icon: <FiInbox size={18} />,
      name: "Pending Orders",
      path: "/admin/warehouse/orders",
      section: "ORDERS",
    },
    {
      icon: <FiShoppingCart size={18} />,
      name: "Orders",
      path: "#",
      children: [
        { name: "All Orders", path: "/admin/order/list" },
        { name: "Create Order", path: "/admin/order/add" },
      ],
    },
    {
      icon: <FiUsers size={18} />,
      name: "Customers",
      path: "#",
      children: [
        { name: "All Customers", path: "/admin/customers/list" },
        { name: "Add Customer", path: "/admin/customer/add" },
      ],
    },
    {
      icon: <FiCreditCard size={18} />,
      name: "Payments",
      path: "/admin/payments",
    },
    {
      icon: <FiDollarSign size={18} />,
      name: "COD Cash",
      path: "/admin/cash-management",
    },

    // ── WAREHOUSE & DELIVERY ──────────────────────────────
    {
      icon: <FiArchive size={18} />,
      name: "Warehouse",
      path: "#",
      section: "WAREHOUSE",
      children: [
        { name: "Dashboard", path: "/admin/warehouse" },
        { name: "Pending Orders", path: "/admin/warehouse/orders" },
        { name: "Assignments", path: "/admin/warehouse/assignments" },
        { name: "Inventory", path: "/admin/warehouse/inventory" },
        { name: "Stock Movement", path: "/admin/warehouse/stock-movement" },
        { name: "Stock History", path: "/admin/warehouse/stock-history" },
        { name: "Low Stock Alerts", path: "/admin/warehouse/low-stock" },
        { name: "Stock Report", path: "/admin/warehouse/report" },
        // Warehouse master + serviceable-pincodes are part of the
        // same WAREHOUSE mental section — they configure the dark-
        // stores that the inventory ops above run against.
        { name: "All Warehouses", path: "/admin/warehouses" },
        { name: "Low Stock (all stores)", path: "/admin/warehouses/low-stock" },
        { name: "Serviceable Pincodes", path: "/admin/serviceable-pincodes" },
      ],
    },
    {
      icon: <FiTruck size={18} />,
      name: "Delivery Boys",
      path: "#",
      children: [
        { name: "All Delivery Boys", path: "/admin/delivery-boys/list" },
        { name: "Add Delivery Boy", path: "/admin/delivery-boys/add" },
      ],
    },

    // ── CUSTOMER SERVICE ──────────────────────────────────
    // Tickets + return RMA queue.
    {
      icon: <FiLifeBuoy size={18} />,
      name: "Support",
      path: "#",
      section: "CUSTOMER SERVICE",
      children: [
        { name: "Support Tickets", path: "/admin/support-ticket/list" },
        { name: "Return Requests", path: "/admin/return-requests" },
      ],
    },

    // ── CATALOG ───────────────────────────────────────────
    // Products, categories, brands. Sits lower because day-to-day
    // ops don't touch these — they're set up once and updated rarely.
    {
      icon: <FiPackage size={18} />,
      name: "Products",
      path: "#",
      section: "CATALOG",
      children: [
        { name: "All Products", path: "/admin/product/list" },
        { name: "Add Product", path: "/admin/product/add" },
        { name: "Bulk CSV Upload", path: "/admin/product/bulk-upload" },
      ],
    },
    {
      icon: <FiLayers size={18} />,
      name: "Categories",
      path: "#",
      children: [
        { name: "All Categories", path: "/admin/category/list" },
        { name: "Add Category", path: "/admin/category/add" },
      ],
    },
    {
      icon: <FiTag size={18} />,
      name: "Brands",
      path: "#",
      children: [
        { name: "All Brands", path: "/admin/brand/list" },
        { name: "Add Brand", path: "/admin/brand/add" },
      ],
    },

    // ── MARKETING ─────────────────────────────────────────
    // Campaigns (price-driven), loyalty (VIP), and outbound
    // customer comms (push/SMS/WhatsApp/email) live together because
    // they share the same "talking to customers" intent — even
    // though the implementations are different.
    {
      icon: <FiZap size={18} />,
      name: "Marketing",
      path: "#",
      section: "MARKETING",
      children: [
        { name: "Promo Codes", path: "/admin/promo-codes/list" },
        { name: "Flash Sales", path: "/admin/flash-sales" },
        { name: "Featured Sections", path: "/admin/featured-sections" },
        { name: "Home Banners", path: "/admin/home-banners" },
      ],
    },
    {
      icon: <FiAward size={18} />,
      name: "VIP Wallets",
      path: "/admin/vip",
    },
    {
      icon: <FiBell size={18} />,
      name: "Customer Comms",
      path: "#",
      children: [
        { name: "Push Notifications", path: "/admin/send-notification" },
        { name: "Newsletter", path: "/admin/newsletter-management" },
        { name: "WhatsApp Templates", path: "/admin/whatsapp" },
      ],
    },

    // ── CONTENT ───────────────────────────────────────────
    {
      icon: <FiFileText size={18} />,
      name: "Content",
      path: "#",
      section: "CONTENT",
      children: [
        { name: "Blog Posts", path: "/admin/blog" },
        { name: "Media Library", path: "/admin/media" },
        { name: "Legal Pages", path: "/admin/legal-policies" },
      ],
    },

    // ── INSIGHTS ──────────────────────────────────────────
    {
      icon: <FiBarChart2 size={18} />,
      name: "Analytics",
      path: "/admin/analytics",
      section: "INSIGHTS",
    },
    {
      icon: <FiFileText size={18} />,
      name: "Reports",
      path: "/admin/reports",
    },

    // ── SYSTEM ────────────────────────────────────────────
    {
      icon: <FiSettings size={18} />,
      name: "Settings",
      path: "#",
      section: "SYSTEM",
      children: [
        { name: "General Settings", path: "/admin/settings" },
        { name: "App Settings", path: "/admin/app-settings" },
        { name: "App Releases (APK)", path: "/admin/app-releases" },
        { name: "FCM Push (Notifications)", path: "/admin/fcm-settings" },
      ],
    },
    {
      icon: <FiUserCheck size={18} />,
      name: "Admin Users",
      path: "#",
      children: [
        { name: "All Admins", path: "/admin/users/list" },
        { name: "Add Admin", path: "/admin/user/register" },
        { name: "Roles", path: "/admin/roles" },
      ],
    },
    {
      icon: <FiActivity size={18} />,
      name: "Change Log",
      path: "/admin/audit-logs",
    },
    {
      icon: <FiHelpCircle size={18} />,
      name: "Help & Docs",
      path: "#",
      children: [
        { name: "Order Flow Diagram", path: "/admin/docs/order-flow" },
        { name: "Order Management Guide", path: "/admin/docs/order-management" },
      ],
    },
    {
      icon: <FiTrash2 size={18} />,
      name: "Trash",
      path: "/admin/trash",
    },
    // Profile lives in the header user dropdown (top-right). Don't add
    // it here too — keep navigation (sidebar) and account actions
    // (header) cleanly separated.
  ], []);

  useEffect(() => {
    const currentMenu = menu.find((item) =>
      item.children?.some((child) => location.pathname === child.path)
    );
    setOpenMenu(currentMenu?.name || null);
  }, [location.pathname, menu]);

  const renderMenuItem = (item: MenuItem, index: number) => {
    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openMenu === item.name;
    const isActive = location.pathname === item.path;
    const isChildActive = item.children?.some((sub) => location.pathname === sub.path);
    const toggleMenu = () => setOpenMenu(isOpen ? null : item.name);

    return (
      <div key={index}>
        {/* Section label */}
        {item.section && !isCollapsed && index > 0 && (
          <div className="px-4 pt-5 pb-1">
            <span className="text-[10px] font-semibold tracking-widest text-slate-400 dark:text-slate-500 uppercase">
              {item.section}
            </span>
          </div>
        )}

        {hasChildren ? (
          <button
            onClick={toggleMenu}
            className={`w-full flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-sm cursor-pointer transition-colors duration-150 ${
              isChildActive
                ? "bg-primary-500/10 text-primary-500 dark:text-primary-400 font-medium"
                : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200"
            }`}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="flex-shrink-0">{item.icon}</span>
            {!isCollapsed && (
              <>
                <span className="flex-1 text-left truncate">{label(item.name)}</span>
                <FiChevronDown
                  size={14}
                  className={`flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                />
              </>
            )}
          </button>
        ) : (
          <Link
            to={item.path}
            className={`flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-sm transition-colors duration-150 ${
              isActive
                ? "bg-primary-500/10 text-primary-500 dark:text-primary-400 font-medium"
                : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-200"
            }`}
            style={{ width: 'calc(100% - 16px)' }}
          >
            <span className="flex-shrink-0">{item.icon}</span>
            {!isCollapsed && <span className="truncate">{label(item.name)}</span>}
          </Link>
        )}

        {/* Submenu */}
        {!isCollapsed && hasChildren && (
          <div className={`overflow-hidden transition-all duration-200 ease-in-out ${
            isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
          }`}>
            <div className="ml-[26px] pl-4 border-l border-slate-200 dark:border-slate-700 py-1 mr-2">
              {item.children!.map((sub, subIndex) => (
                <Link
                  to={sub.path}
                  key={subIndex}
                  className={`block px-3 py-1.5 text-[13px] rounded-md transition-colors duration-150 ${
                    location.pathname === sub.path
                      ? "text-primary-500 dark:text-primary-400 font-medium bg-primary-500/5"
                      : "text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  }`}
                >
                  {sub.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <aside
      className={`h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 transition-[width] duration-300 ease-in-out flex flex-col ${
        isCollapsed ? "w-[60px]" : "w-[248px]"
      }`}
    >
      {/* Logo */}
      <div className="flex items-center h-14 px-4 border-b border-slate-200 dark:border-slate-800 flex-shrink-0">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-sm">A</span>
          </div>
          <span
            className={`font-semibold text-base text-slate-900 dark:text-white transition-all duration-300 truncate ${
              isCollapsed ? "opacity-0 w-0 overflow-hidden" : "opacity-100 w-auto"
            }`}
          >
            Raitha Bazar Admin
          </span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-thin">
        {menu.map(renderMenuItem)}
      </nav>

      {/* Footer */}
      <div className={`flex-shrink-0 px-4 py-3 border-t border-slate-200 dark:border-slate-800 transition-all duration-300 ${
        isCollapsed ? "opacity-0" : "opacity-100"
      }`}>
        <p className="text-[11px] text-slate-400 dark:text-slate-500 text-center">
          Raitha Bazar Admin v1.0
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
